import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckSquare, Activity, Mic, Camera, FileSignature,
  AlertTriangle, CloudUpload, CheckCircle2, Shield,
} from 'lucide-react';

/* ─── Soglie Parametri Vitali ─── */
const soglie = {
  pressMax: { min: 90, max: 160, unita: 'mmHg', label: 'PA Sistolica' },
  pressMin: { min: 60, max: 100, unita: 'mmHg', label: 'PA Diastolica' },
  fc:       { min: 50, max: 110, unita: 'bpm',  label: 'Freq. Cardiaca' },
  spo2:     { min: 90, max: 100, unita: '%',     label: 'SpO2' },
  temp:     { min: 35.0, max: 38.5, unita: '°C', label: 'Temperatura' },
};

function isCritico(campo, valore) {
  if (!valore || valore === '') return false;
  const num = parseFloat(valore);
  if (isNaN(num)) return false;
  const s = soglie[campo];
  return num < s.min || num > s.max;
}

/* ─── SECTION A: Checklist Prestazioni ─── */
function SezioneChecklist({ prestazioni, completate, onToggle }) {
  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="section-checklist">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-xl bg-primary/8 flex items-center justify-center">
            <CheckSquare className="w-4 h-4 text-primary" />
          </div>
          <h2 className="text-sm font-bold text-foreground">Checklist Prestazioni</h2>
          <Badge variant="outline" className="ml-auto text-[10px] px-2 py-0.5 rounded-md text-muted-foreground border-border/40">
            {completate.filter(Boolean).length}/{prestazioni.length}
          </Badge>
        </div>
        <div className="space-y-3">
          {prestazioni.map((p, i) => (
            <div
              key={i}
              className={`flex items-center gap-4 px-4 py-4 rounded-2xl border transition-all duration-200 ${
                completate[i]
                  ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-200/50 dark:border-emerald-800/40'
                  : 'bg-muted/20 border-border/30 hover:bg-muted/40'
              }`}
              data-testid={`checklist-item-${i}`}
            >
              <Switch
                checked={completate[i] || false}
                onCheckedChange={() => onToggle(i)}
                className="scale-125"
                data-testid={`checklist-toggle-${i}`}
              />
              <span className={`text-sm font-medium flex-1 ${
                completate[i] ? 'text-emerald-700 dark:text-emerald-300 line-through' : 'text-foreground'
              }`}>
                {p.descrizione}
              </span>
              {completate[i] && (
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── SECTION B: Parametri Vitali ─── */
function VitalInput({ campo, valore, onChange }) {
  const s = soglie[campo];
  const critico = isCritico(campo, valore);

  return (
    <div data-testid={`vital-input-${campo}`}>
      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
        {s.label}
      </label>
      <div className="relative">
        <input
          type="number"
          inputMode="decimal"
          step={campo === 'temp' ? '0.1' : '1'}
          value={valore}
          onChange={(e) => onChange(campo, e.target.value)}
          placeholder="—"
          className={`w-full h-14 px-4 pr-16 rounded-2xl text-lg font-semibold text-center border-2 transition-all duration-200 bg-background outline-none
            ${critico
              ? 'border-red-400 dark:border-red-600 bg-red-50/50 dark:bg-red-950/20 text-red-700 dark:text-red-300 ring-2 ring-red-200 dark:ring-red-800/50'
              : 'border-border/40 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 text-foreground'
            }
          `}
          data-testid={`vital-field-${campo}`}
        />
        <span className={`absolute right-4 top-1/2 -translate-y-1/2 text-xs font-medium ${
          critico ? 'text-red-500' : 'text-muted-foreground'
        }`}>
          {s.unita}
        </span>
      </div>
      <AnimatePresence>
        {critico && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2"
          >
            <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200/60 dark:border-red-800/40" data-testid={`vital-alert-${campo}`}>
              <AlertTriangle className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
              <p className="text-[11px] text-red-700 dark:text-red-300 leading-snug font-medium">
                Valore critico. Verra inviato un alert automatico al Case Manager alla chiusura del referto.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SezioneParametri({ valori, onChange }) {
  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="section-vitals">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-xl bg-rose-50 dark:bg-rose-950/30 flex items-center justify-center">
            <Activity className="w-4 h-4 text-rose-600 dark:text-rose-400" />
          </div>
          <h2 className="text-sm font-bold text-foreground">Parametri Vitali</h2>
        </div>

        {/* Pressione in row */}
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
          Pressione Arteriosa
        </p>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <VitalInput campo="pressMax" valore={valori.pressMax} onChange={onChange} />
          <VitalInput campo="pressMin" valore={valori.pressMin} onChange={onChange} />
        </div>

        {/* Other vitals in grid */}
        <div className="grid grid-cols-3 gap-3">
          <VitalInput campo="fc" valore={valori.fc} onChange={onChange} />
          <VitalInput campo="spo2" valore={valori.spo2} onChange={onChange} />
          <VitalInput campo="temp" valore={valori.temp} onChange={onChange} />
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── SECTION C: Scala Dolore NRS ─── */
function SezioneScalaDolore({ valore, onChange }) {
  const getColor = (n) => {
    if (n <= 2) return 'bg-emerald-500 text-white';
    if (n <= 4) return 'bg-yellow-400 text-yellow-900';
    if (n <= 6) return 'bg-orange-400 text-white';
    if (n <= 8) return 'bg-red-400 text-white';
    return 'bg-red-600 text-white';
  };

  const getLabel = (n) => {
    if (n === 0) return 'Nessun dolore';
    if (n <= 3) return 'Lieve';
    if (n <= 6) return 'Moderato';
    if (n <= 8) return 'Severo';
    return 'Insopportabile';
  };

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="section-pain-scale">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-xl bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center">
            <Shield className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-sm font-bold text-foreground">Scala del Dolore (NRS)</h2>
          {valore !== null && (
            <Badge className={`ml-auto text-xs px-2.5 py-0.5 rounded-lg ${getColor(valore)}`} data-testid="pain-badge">
              {valore}/10 — {getLabel(valore)}
            </Badge>
          )}
        </div>
        <div className="flex gap-1.5 flex-wrap" data-testid="pain-scale-buttons">
          {Array.from({ length: 11 }, (_, i) => (
            <button
              key={i}
              data-testid={`pain-btn-${i}`}
              onClick={() => onChange(i)}
              className={`
                w-12 h-12 rounded-xl text-sm font-bold transition-all duration-200 border-2
                ${valore === i
                  ? `${getColor(i)} border-transparent shadow-md scale-110`
                  : 'bg-muted/30 border-border/30 text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                }
              `}
            >
              {i}
            </button>
          ))}
        </div>
        <div className="flex justify-between mt-2 px-1">
          <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">Nessun dolore</span>
          <span className="text-[10px] text-red-600 dark:text-red-400 font-medium">Massimo dolore</span>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── SECTION D: Note e Allegati ─── */
function SezioneNote({ note, onNoteChange }) {
  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="section-notes">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-xl bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center">
            <FileSignature className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-sm font-bold text-foreground">Note Cliniche</h2>
        </div>

        <div className="relative mb-4">
          <textarea
            value={note}
            onChange={(e) => onNoteChange(e.target.value)}
            placeholder="Descrivi lo svolgimento della visita, osservazioni cliniche, comunicazioni con il paziente/caregiver..."
            rows={5}
            className="w-full px-4 py-4 rounded-2xl border-2 border-border/40 bg-background text-sm text-foreground placeholder:text-muted-foreground/50 resize-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 outline-none transition-all"
            data-testid="clinical-notes-textarea"
          />
          <button
            className="absolute right-3 bottom-3 w-10 h-10 rounded-xl bg-muted/60 border border-border/40 flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            title="Dettatura vocale"
            data-testid="voice-dictation-btn"
          >
            <Mic className="w-4 h-4" />
          </button>
        </div>

        {/* Photo attachment */}
        <button
          className="w-full h-20 rounded-2xl border-2 border-dashed border-border/50 bg-muted/10 hover:bg-muted/30 transition-colors flex items-center justify-center gap-3 text-muted-foreground hover:text-foreground"
          data-testid="photo-attach-btn"
        >
          <Camera className="w-6 h-6" />
          <span className="text-sm font-medium">Scatta Foto (es. lesione cutanea)</span>
        </button>
      </CardContent>
    </Card>
  );
}

/* ─── MAIN COMPONENT ─── */
export default function ClinicalDiaryForm({ prestazioni, onComplete }) {
  const [completate, setCompletateFn] = useState(prestazioni.map(() => false));
  const [valoriVitali, setValoriVitali] = useState({
    pressMax: '', pressMin: '', fc: '', spo2: '', temp: '',
  });
  const [dolore, setDolore] = useState(null);
  const [note, setNote] = useState('');
  const [visitaConclusa, setVisitaConclusa] = useState(false);

  const togglePrestazione = (idx) => {
    setCompletateFn((prev) => prev.map((v, i) => (i === idx ? !v : v)));
  };

  const handleVitaleChange = (campo, valore) => {
    setValoriVitali((prev) => ({ ...prev, [campo]: valore }));
  };

  const hasCritici = Object.entries(valoriVitali).some(([k, v]) => isCritico(k, v));
  const tutteCompletate = completate.every(Boolean);

  const handleConcludi = () => {
    setVisitaConclusa(true);
    if (onComplete) onComplete();
  };

  if (visitaConclusa) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        data-testid="visit-completed-state"
      >
        <Card className="border-0 shadow-[0_2px_8px_rgba(0,0,0,0.08)] rounded-2xl">
          <CardContent className="py-12 flex flex-col items-center text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              className="w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center mb-5"
            >
              <CheckCircle2 className="w-10 h-10 text-white" />
            </motion.div>
            <h2 className="text-xl font-bold text-foreground" data-testid="visit-complete-title">
              Visita Conclusa e Firmata
            </h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-md">
              Il referto e stato salvato e verra sincronizzato nel FSE. I dati sono stati condivisi con l'Equipe di cura.
            </p>
            {hasCritici && (
              <div className="mt-4 px-4 py-3 rounded-2xl bg-red-50 dark:bg-red-950/30 border border-red-200/60 dark:border-red-800/40 max-w-sm">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                  <p className="text-xs text-red-700 dark:text-red-300 font-medium">
                    Alert inviato al Case Manager per valori critici rilevati.
                  </p>
                </div>
              </div>
            )}
            <div className="mt-6 flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
              <CloudUpload className="w-4 h-4" />
              <span className="text-xs font-medium">Sincronizzazione completata</span>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="space-y-5" data-testid="clinical-diary-form">
      {/* Section A */}
      <SezioneChecklist
        prestazioni={prestazioni}
        completate={completate}
        onToggle={togglePrestazione}
      />

      {/* Section B */}
      <SezioneParametri
        valori={valoriVitali}
        onChange={handleVitaleChange}
      />

      {/* Section C */}
      <SezioneScalaDolore
        valore={dolore}
        onChange={setDolore}
      />

      {/* Section D */}
      <SezioneNote
        note={note}
        onNoteChange={setNote}
      />

      {/* Footer: Conclude & Sign */}
      <div className="space-y-3" data-testid="diary-footer">
        {hasCritici && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-red-50 dark:bg-red-950/30 border border-red-200/60 dark:border-red-800/40"
            data-testid="critical-summary-alert"
          >
            <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
            <p className="text-xs text-red-700 dark:text-red-300 font-medium">
              Attenzione: sono presenti valori critici. Alla chiusura verra generato un alert automatico.
            </p>
          </motion.div>
        )}

        <Button
          className="w-full h-16 rounded-2xl text-base font-bold gap-3 shadow-lg hover:shadow-xl transition-all bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500"
          onClick={handleConcludi}
          data-testid="conclude-visit-btn"
        >
          <FileSignature className="w-6 h-6" />
          Concludi Visita e Firma Referto
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          I dati verranno sincronizzati nel FSE e condivisi con l'Equipe.
        </p>
      </div>
    </div>
  );
}
